/**
 * Template Application v2 - 1:1 clone reconstruction using Design Editing API
 * 
 * This service applies descriptor v2 templates to create exact visual replicas
 * with asset reuse/re-upload, richtext preservation, shape recreation, and z-order maintenance.
 */

import { openDesign } from "@canva/design";
import { upload } from "@canva/asset";
import type {
  DescriptorV2,
  Element,
  ApplicationOptions,
  ApplicationResult,
  AssetMapping,
  Fill,
  FontInfo
} from "../types/descriptor-v2";

// ============ Helper Functions ============

/**
 * Check if a font is commonly available in Canva (beyond our hard-coded list)
 * This helps validate fonts from master designs that might not be in our mapping
 */
function isCommonCanvaFont(fontFamily: string): boolean {
  const commonCanvaFonts = [
    // Google Fonts commonly available in Canva
    'Impact', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins', 'Inter',
    'Playfair Display', 'Source Sans Pro', 'Oswald', 'Raleway', 'PT Sans',
    'Merriweather', 'Ubuntu', 'Nunito', 'Rubik', 'Work Sans', 'Fira Sans',
    'DM Sans', 'Quicksand', 'Barlow', 'Manrope', 'Plus Jakarta Sans',
    'Outfit', 'Space Grotesk', 'Lexend', 'Sora', 'Satoshi',
    // Adobe Fonts commonly available
    'Proxima Nova', 'Futura', 'Avenir', 'Minion Pro', 'Myriad Pro',
    // Other popular fonts
    'Londrina Solid', 'Bebas Neue', 'Comfortaa', 'Dancing Script',
    'Pacifico', 'Lobster', 'Righteous', 'Fredoka One'
  ];

  return commonCanvaFonts.includes(fontFamily);
}

/**
 * Get the correct font family name for Canva based on master design fonts
 * Uses resolved font family names for createTextElement as per Canva Apps SDK documentation
 */
function getCanvaFontFamily(requestedFont: string, masterDesignFonts?: FontInfo[], log?: string[]): string {
  if (!requestedFont) {
    return 'Arial';
  }

  // If we have master design fonts, prioritize them (they should already be resolved to font family names)
  if (masterDesignFonts && masterDesignFonts.length > 0) {
    // Find by family name (which should be the resolved font family name)
    const masterFontByFamily = masterDesignFonts.find(f => f.family === requestedFont);
    if (masterFontByFamily) {
      log?.push(`[apply-v2] 🎯 MASTER FONT FOUND: "${requestedFont}" from master design`);
      return masterFontByFamily.family; // This is the resolved font family name
    }

    // Try to find by display name as fallback
    const masterFontByDisplay = masterDesignFonts.find(f => f.displayName === requestedFont);
    if (masterFontByDisplay) {
      log?.push(`[apply-v2] 🎯 MASTER FONT DISPLAY FOUND: "${requestedFont}" (by display name)`);
      return masterFontByDisplay.family; // This is the resolved font family name
    }
  }

  // Check if font exists in our Canva mapping (fallback for non-master fonts)
  if (CANVA_AVAILABLE_FONTS[requestedFont]) {
    log?.push(`[apply-v2] ✅ CANVA FONT AVAILABLE: "${requestedFont}"`);
    return CANVA_AVAILABLE_FONTS[requestedFont];
  }

  log?.push(`[apply-v2] ⚠️ FONT FALLBACK: "${requestedFont}" → Arial`);
  return 'Arial';
}

/**
 * Map font weight values to valid Canva font weight values
 * Based on Canva Apps SDK documentation
 */
function mapFontWeight(weight?: string): string {
  if (!weight) return 'normal';

  // Normalize the weight value
  const normalizedWeight = weight.toLowerCase().trim();

  // Map various weight values to valid Canva values
  const weightMapping: Record<string, string> = {
    // Standard CSS values
    'normal': 'normal',
    'bold': 'bold',

    // Numeric values
    '100': 'normal',
    '200': 'normal',
    '300': 'normal',
    '400': 'normal',
    '500': 'normal',
    '600': 'bold',
    '700': 'bold',
    '800': 'bold',
    '900': 'bold',

    // Named values
    'thin': 'normal',
    'light': 'normal',
    'regular': 'normal',
    'medium': 'normal',
    'semibold': 'bold',
    'heavy': 'bold',  // ← This is the key fix!
    'black': 'bold',
    'extra-bold': 'bold',
    'ultra-bold': 'bold'
  };

  return weightMapping[normalizedWeight] || 'normal';
}

/**
 * Get an intelligent font fallback based on font characteristics
 * Analyzes the font name to determine appropriate substitutes
 */
function getIntelligentFontFallback(fontFamily: string): string {
  const lowerFamily = fontFamily.toLowerCase();

  // Display/Decorative fonts
  if (lowerFamily.includes('display') || lowerFamily.includes('decorative') ||
      lowerFamily.includes('fancy') || lowerFamily.includes('script')) {
    return 'Playfair Display';
  }

  // Sans-serif indicators
  if (lowerFamily.includes('sans') || lowerFamily.includes('gothic') ||
      lowerFamily.includes('grotesk') || lowerFamily.includes('helvetica')) {
    return 'Open Sans';
  }

  // Serif indicators
  if (lowerFamily.includes('serif') || lowerFamily.includes('times') ||
      lowerFamily.includes('garamond') || lowerFamily.includes('minion')) {
    return 'Merriweather';
  }

  // Monospace indicators
  if (lowerFamily.includes('mono') || lowerFamily.includes('code') ||
      lowerFamily.includes('courier') || lowerFamily.includes('console')) {
    return 'Courier New';
  }

  // Condensed/Narrow fonts
  if (lowerFamily.includes('condensed') || lowerFamily.includes('narrow') ||
      lowerFamily.includes('compressed')) {
    return 'Oswald';
  }

  // Bold/Heavy fonts
  if (lowerFamily.includes('bold') || lowerFamily.includes('heavy') ||
      lowerFamily.includes('black') || lowerFamily.includes('extra')) {
    return 'Montserrat';
  }

  // Light/Thin fonts
  if (lowerFamily.includes('light') || lowerFamily.includes('thin') ||
      lowerFamily.includes('ultra')) {
    return 'Lato';
  }

  // Rounded fonts
  if (lowerFamily.includes('round') || lowerFamily.includes('soft')) {
    return 'Nunito';
  }

  // Default fallback
  return fontFamily; // Return original if no pattern matches
}

/**
 * Map text alignment values to Canva API format
 */
function mapTextAlign(align: string | undefined): string {
  switch (align?.toLowerCase()) {
    case 'left':
    case 'start':
      return 'start';
    case 'center':
    case 'middle':
      return 'center';
    case 'right':
    case 'end':
      return 'end';
    case 'justify':
      return 'justify';
    default:
      return 'start'; // Default to start instead of left
  }
}

/**
 * TASK 1: Enforce exact page/canvas size using Canva SDK
 */
async function enforceExactPageSize(
  session: any,
  targetWidth: number,
  targetHeight: number,
  log: string[]
): Promise<void> {
  try {
    const currentWidth = session.page.width;
    const currentHeight = session.page.height;

    log.push(`[apply-v2] Current page size: ${currentWidth}x${currentHeight}px`);
    log.push(`[apply-v2] Target page size: ${targetWidth}x${targetHeight}px`);

    // Check if page is already the correct size
    if (currentWidth === targetWidth && currentHeight === targetHeight) {
      log.push(`[apply-v2] ✅ Page already has correct size: ${targetWidth}x${targetHeight}px`);
      return;
    }

    log.push(`[apply-v2] Attempting to resize page from ${currentWidth}x${currentHeight}px to ${targetWidth}x${targetHeight}px`);

    // Try to resize current page
    if (session.page.resize) {
      await session.page.resize({
        width: targetWidth,
        height: targetHeight
      });

      // Verify resize worked
      const newWidth = session.page.width;
      const newHeight = session.page.height;

      if (newWidth === targetWidth && newHeight === targetHeight) {
        log.push(`[apply-v2] ✅ Page resized successfully to ${targetWidth}x${targetHeight}px`);

        // Set page flag to ensure idempotency
        await setPageData(session, {
          photoBook: {
            v2: {
              sizeApplied: true,
              appliedWidth: targetWidth,
              appliedHeight: targetHeight,
              appliedAt: new Date().toISOString()
            }
          }
        }, log);

        return;
      } else {
        log.push(`[apply-v2] Page resize verification failed: expected ${targetWidth}x${targetHeight}px, got ${newWidth}x${newHeight}px`);
      }
    } else {
      log.push(`[apply-v2] session.page.resize method not available`);
    }

    // Fallback: try creating new page with exact dimensions
    if (session.design?.addPage) {
      log.push(`[apply-v2] Attempting to create new page with ${targetWidth}x${targetHeight}px`);

      const newPage = await session.design.addPage({
        width: targetWidth,
        height: targetHeight
      });

      if (newPage) {
        // Switch to new page
        await session.setCurrentPage(newPage);
        log.push(`[apply-v2] ✅ Created and switched to new page with ${targetWidth}x${targetHeight}px`);

        // Set page flag
        await setPageData(session, {
          photoBook: {
            v2: {
              sizeApplied: true,
              appliedWidth: targetWidth,
              appliedHeight: targetHeight,
              appliedAt: new Date().toISOString()
            }
          }
        }, log);

        return;
      }
    }

    // Final fallback: proceed with current page size and warn user
    const finalWidth = session.page.width;
    const finalHeight = session.page.height;

    log.push(`[apply-v2] ⚠️  WARNING: Could not resize page to ${targetWidth}x${targetHeight}px`);
    log.push(`[apply-v2] ⚠️  Proceeding with current page size: ${finalWidth}x${finalHeight}px`);
    log.push(`[apply-v2] ⚠️  Template elements may be scaled or positioned incorrectly`);

    // Don't throw error - let the application continue with scaling adjustments

  } catch (error) {
    log.push(`[apply-v2] ❌ Page size enforcement failed: ${error.message}`);

    // Final fallback: proceed with current page size
    const finalWidth = session.page.width;
    const finalHeight = session.page.height;

    log.push(`[apply-v2] ⚠️  Proceeding with current page size: ${finalWidth}x${finalHeight}px due to error`);
    // Don't re-throw - allow application to continue
  }
}

/**
 * Set page data for tracking V2 application state
 */
async function setPageData(session: any, data: any, log: string[]): Promise<void> {
  try {
    if (session.page.setData) {
      await session.page.setData(data);
      log.push(`[apply-v2] Set page data: ${JSON.stringify(data)}`);
    } else {
      log.push(`[apply-v2] Warning: setData not available, skipping page data`);
    }
  } catch (error) {
    log.push(`[apply-v2] Warning: Failed to set page data: ${error.message}`);
  }
}

/**
 * Mark page as having V2 template applied for idempotency
 */
async function markPageAsV2Applied(session: any, descriptor: DescriptorV2, log: string[]): Promise<void> {
  try {
    const templateSlug = `${descriptor.meta.sourceDesignId}-v2`;
    await setPageData(session, {
      photoBook: {
        v2: {
          appliedFrom: templateSlug,
          appliedAt: new Date().toISOString(),
          sourceDesignId: descriptor.meta.sourceDesignId
        }
      }
    }, log);
    log.push(`[apply-v2] Marked page as V2 applied: ${templateSlug}`);
  } catch (error) {
    log.push(`[apply-v2] Warning: Failed to mark page as V2 applied: ${error.message}`);
  }
}

/**
 * TASK 5: Enhanced font resolver with comprehensive Canva font support
 */
const CANVA_AVAILABLE_FONTS: Record<string, string> = {
  // System fonts (always available)
  'Arial': 'Arial',
  'Helvetica': 'Arial', // Map to Arial as closest match
  'Helvetica Neue': 'Arial',
  'Times': 'Times New Roman',
  'Times New Roman': 'Times New Roman',
  'Courier': 'Courier New',
  'Courier New': 'Courier New',
  'Verdana': 'Verdana',
  'Georgia': 'Georgia',
  'Trebuchet MS': 'Trebuchet MS',
  'Comic Sans MS': 'Comic Sans MS',
  'Impact': 'Impact',
  'Londrina Solid': 'Londrina Solid',

  // Popular Google Fonts (commonly available in Canva)
  'Montserrat': 'Montserrat',
  'Open Sans': 'Open Sans',
  'Roboto': 'Roboto',
  'Lato': 'Lato',
  'Poppins': 'Poppins',
  'Inter': 'Inter',
  'Playfair Display': 'Playfair Display',
  'Source Sans Pro': 'Source Sans Pro',
  'Nunito': 'Nunito',
  'Oswald': 'Oswald',
  'Raleway': 'Raleway',
  'PT Sans': 'PT Sans',
  'Ubuntu': 'Ubuntu',
  'Merriweather': 'Merriweather',
  'Noto Sans': 'Noto Sans',
  'Fira Sans': 'Fira Sans',
  'Work Sans': 'Work Sans',
  'Libre Baskerville': 'Libre Baskerville',
  'Crimson Text': 'Crimson Text',
  'Libre Franklin': 'Libre Franklin',
  'Quicksand': 'Quicksand',
  'Rubik': 'Rubik',
  'DM Sans': 'DM Sans',

  // Display/Decorative fonts
  'Manrope': 'Manrope',
  'Space Grotesk': 'Space Grotesk',
  'Plus Jakarta Sans': 'Plus Jakarta Sans',
  'Outfit': 'Outfit',
  'Lexend': 'Lexend',

  // Additional common fonts
  'Avenir': 'Montserrat', // Similar geometric sans
  'Futura': 'Montserrat', // Similar geometric sans
  'Proxima Nova': 'Montserrat', // Similar modern sans
  'Gotham': 'Montserrat', // Similar geometric sans
  'Minion Pro': 'Merriweather', // Similar serif
  'Garamond': 'Libre Baskerville', // Similar serif
  'Caslon': 'Libre Baskerville', // Similar serif
  'Baskerville': 'Libre Baskerville', // Direct match
  'Gill Sans': 'Open Sans', // Similar humanist sans
  'Optima': 'Open Sans', // Similar humanist sans
  'Myriad Pro': 'Open Sans', // Similar humanist sans
};

interface FontResolutionResult {
  resolvedFamily: string;
  isExact: boolean;
  isFallback: boolean;
  originalFamily: string;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
}

/**
 * Simple font resolver that prioritizes master design fonts
 * Based on Canva Apps SDK documentation for text element creation
 */
function resolveFontFamily(requestedFamily: string, log: string[], masterDesignFonts?: FontInfo[]): FontResolutionResult {
  if (!requestedFamily || typeof requestedFamily !== 'string') {
    log.push(`[apply-v2] 🚨 FONT ERROR: Invalid font family "${requestedFamily}" → Arial (emergency fallback)`);
    return {
      resolvedFamily: 'Arial',
      isExact: false,
      isFallback: true,
      originalFamily: requestedFamily || 'undefined',
      confidence: 'low',
      reason: 'Invalid font family provided'
    };
  }

  // Use the font family resolution function
  const resolvedFont = getCanvaFontFamily(requestedFamily, masterDesignFonts, log);

  // Determine properties
  const isFromMaster = masterDesignFonts?.some(f => f.family === requestedFamily) || false;
  const isExact = resolvedFont === requestedFamily;
  const isFallback = resolvedFont !== requestedFamily;

  return {
    resolvedFamily: resolvedFont,
    isExact: isExact,
    isFallback: isFallback,
    originalFamily: requestedFamily,
    confidence: isFromMaster ? 'high' : (isExact ? 'high' : 'medium'),
    reason: isFromMaster ? 'Master design font preserved' : (isExact ? 'Exact Canva font match' : 'Font fallback applied')
  };
}

/**
 * Test if a font family is actually available in Canva by attempting to create a test element
 */
async function testFontAvailability(fontFamily: string, session: any, log: string[]): Promise<boolean> {
  try {
    // Create a minimal test text element to check font availability
    const testConfig = {
      left: 0,
      top: 0,
      width: 100,
      height: 50,
      text: {
        regions: [{
          text: 'Test',
          formatting: {
            fontFamily: fontFamily,
            fontSize: 16,
            color: '#000000'
          }
        }]
      }
    };

    const testElement = session.helpers.elementStateBuilder.createTextElement(testConfig);
    const insertedTest = session.page.elements.insertAfter(undefined, testElement);

    // Check if the font was actually applied
    if (insertedTest?.text?.readTextRegions) {
      const testRegions = insertedTest.text.readTextRegions();
      const appliedFont = testRegions[0]?.formatting?.fontFamily;

      // Clean up test element
      insertedTest.delete();

      const isAvailable = appliedFont === fontFamily;
      log.push(`[apply-v2] 🧪 FONT TEST: "${fontFamily}" → "${appliedFont}" ${isAvailable ? '✅ Available' : '❌ Not available'}`);
      return isAvailable;
    }

    // Clean up test element if readTextRegions failed
    insertedTest?.delete();
    return false;
  } catch (error) {
    log.push(`[apply-v2] 🧪 FONT TEST FAILED: "${fontFamily}" - ${error.message}`);
    return false;
  }
}

/**
 * Validate and adjust text metrics for parity
 */
async function validateTextMetrics(
  element: any,
  expectedWidth: number,
  expectedHeight: number,
  log: string[]
): Promise<void> {
  try {
    if (!element.getBoundingBox) {
      log.push(`[apply-v2] Warning: Cannot validate text metrics - getBoundingBox not available`);
      return;
    }

    const bbox = await element.getBoundingBox();
    const actualWidth = bbox.width;
    const actualHeight = bbox.height;

    const widthDelta = Math.abs(actualWidth - expectedWidth);
    const heightDelta = Math.abs(actualHeight - expectedHeight);

    log.push(`[apply-v2] Text metrics: expected ${expectedWidth}x${expectedHeight}px, actual ${actualWidth}x${actualHeight}px`);
    log.push(`[apply-v2] Text metrics delta: width=${widthDelta.toFixed(1)}px, height=${heightDelta.toFixed(1)}px`);

    if (widthDelta > 1 || heightDelta > 1) {
      log.push(`[apply-v2] Warning: Text metrics delta exceeds 1px threshold`);
      // TODO: Implement micro-adjustments for letter spacing or box width
    }

  } catch (error) {
    log.push(`[apply-v2] Warning: Text metrics validation failed: ${error.message}`);
  }
}

/**
 * TASK 4: Spine & hinge placement (mm-precise)
 */
interface SpineHingeLayout {
  leftPageStart: number;
  leftPageEnd: number;
  leftHingeStart: number;
  leftHingeEnd: number;
  spineStart: number;
  spineEnd: number;
  rightHingeStart: number;
  rightHingeEnd: number;
  rightPageStart: number;
  rightPageEnd: number;
}

/**
 * Calculate spine and hinge positions from mm specifications
 */
function calculateSpineHingeLayout(
  descriptor: DescriptorV2,
  log: string[]
): SpineHingeLayout {
  const { coverSizeMm, sourceScalePxPerMm } = descriptor.meta;

  // Default values if not specified in descriptor
  const spreadWidthMm = coverSizeMm.widthMm || 420; // A4 spread
  const pageWidthMm = (spreadWidthMm - (coverSizeMm.spineWidthMm || 0)) / 2;
  const spineWidthMm = coverSizeMm.spineWidthMm || 10;
  const hingeWidthLeftMm = coverSizeMm.hingeWidthLeftMm || 5;
  const hingeWidthRightMm = coverSizeMm.hingeWidthRightMm || 5;

  log.push(`[apply-v2] Cover layout (mm): spread=${spreadWidthMm}, page=${pageWidthMm}, spine=${spineWidthMm}, hingeL=${hingeWidthLeftMm}, hingeR=${hingeWidthRightMm}`);

  // Convert to pixels
  const leftPageStart = 0;
  const leftPageEnd = Math.round(pageWidthMm * sourceScalePxPerMm);
  const leftHingeStart = leftPageEnd;
  const leftHingeEnd = Math.round(leftHingeStart + (hingeWidthLeftMm * sourceScalePxPerMm));
  const spineStart = leftHingeEnd;
  const spineEnd = Math.round(spineStart + (spineWidthMm * sourceScalePxPerMm));
  const rightHingeStart = spineEnd;
  const rightHingeEnd = Math.round(rightHingeStart + (hingeWidthRightMm * sourceScalePxPerMm));
  const rightPageStart = rightHingeEnd;
  const rightPageEnd = Math.round(rightPageStart + (pageWidthMm * sourceScalePxPerMm));

  const layout: SpineHingeLayout = {
    leftPageStart,
    leftPageEnd,
    leftHingeStart,
    leftHingeEnd,
    spineStart,
    spineEnd,
    rightHingeStart,
    rightHingeEnd,
    rightPageStart,
    rightPageEnd
  };

  log.push(`[apply-v2] Spine/hinge layout (px): leftPage=[${leftPageStart}-${leftPageEnd}], leftHinge=[${leftHingeStart}-${leftHingeEnd}], spine=[${spineStart}-${spineEnd}], rightHinge=[${rightHingeStart}-${rightHingeEnd}], rightPage=[${rightPageStart}-${rightPageEnd}]`);

  return layout;
}

/**
 * TASK 7: Robustness & idempotency - handle re-application
 */
async function handleV2Idempotency(
  session: any,
  descriptor: DescriptorV2,
  options: ApplicationOptions,
  log: string[]
): Promise<void> {
  try {
    const templateSlug = `${descriptor.meta.sourceDesignId}-v2`;
    const pageData = session.page.data || {};
    const photoBookData = pageData.photoBook || {};
    const v2Data = photoBookData.v2 || {};

    // Check if this template was already applied
    if (v2Data.appliedFrom === templateSlug) {
      log.push(`[apply-v2] Template ${templateSlug} already applied, handling re-application`);

      if (!options.keepUserContent) {
        // Remove previous V2 template elements
        await removeV2TemplateElements(session, templateSlug, log);
      } else {
        log.push(`[apply-v2] Keeping user content, will overlay new template`);
      }
    } else {
      log.push(`[apply-v2] First application of template ${templateSlug}`);

      if (!options.keepUserContent) {
        // Remove any existing template elements
        await removeTemplateElements(session, log);
      }
    }

  } catch (error) {
    log.push(`[apply-v2] Warning: Idempotency handling failed: ${error.message}`);
  }
}

/**
 * Remove V2 template elements for re-application
 */
async function removeV2TemplateElements(
  session: any,
  templateSlug: string,
  log: string[]
): Promise<void> {
  try {
    const elementsToRemove = session.page.elements.filter((element: any) => {
      return element.metadata?.templateSlug === templateSlug ||
             element.metadata?.templateOwned === true ||
             element.metadata?.v2Template === true;
    });

    log.push(`[apply-v2] Removing ${elementsToRemove.length} existing V2 template elements`);

    elementsToRemove.forEach((element: any) => {
      try {
        session.page.elements.delete(element);
      } catch (deleteError) {
        log.push(`[apply-v2] Warning: Failed to delete element: ${deleteError.message}`);
      }
    });

    log.push(`[apply-v2] ✅ Removed existing V2 template elements`);

  } catch (error) {
    log.push(`[apply-v2] Warning: Failed to remove V2 template elements: ${error.message}`);
  }
}

// ============ Core Application Function ============

/**
 * Apply a descriptor v2 template to the current design
 */
export async function applyTemplateV2(
  descriptor: DescriptorV2,
  options: ApplicationOptions = {
    mode: 'exact',
    keepUserContent: true,
    createNewPage: false,
    assetStrategy: 'auto'
  }
): Promise<ApplicationResult> {
  const log: string[] = [];
  const errors: string[] = [];
  const assetMappings: AssetMapping[] = [];
  
  log.push(`[apply-v2] Starting template application: ${descriptor.layers.length} elements`);
  log.push(`[apply-v2] Mode: ${options.mode}, Keep user content: ${options.keepUserContent}`);

  return new Promise((resolve, reject) => {
    openDesign({ type: "current_page" }, async (session) => {
      try {
        // Validate page type
        if (session.page.type !== "absolute") {
          throw new Error(`Cannot apply template to page type: ${session.page.type}`);
        }

        if (session.page.locked) {
          throw new Error("Cannot apply template to locked page");
        }

        // TASK 1: Enforce exact page/canvas size (no implicit scaling)
        const sourceWidth = descriptor.meta.sourcePageSizePx.widthPx;
        const sourceHeight = descriptor.meta.sourcePageSizePx.heightPx;
        log.push(`[apply-v2] Target page size: ${sourceWidth}x${sourceHeight}px`);

        // Check if page size enforcement is already applied
        const pageData = session.page.data || {};
        const photoBookData = pageData.photoBook || {};
        const v2Data = photoBookData.v2 || {};

        if (v2Data.sizeApplied && v2Data.appliedWidth === sourceWidth && v2Data.appliedHeight === sourceHeight) {
          log.push(`[apply-v2] Page size already enforced: ${sourceWidth}x${sourceHeight}px`);
        } else {
          log.push(`[apply-v2] Enforcing exact page size: ${sourceWidth}x${sourceHeight}px`);
          await enforceExactPageSize(session, sourceWidth, sourceHeight, log);
        }

        // Get current page dimensions using multiple methods
        let currentWidth = session.page.width;
        let currentHeight = session.page.height;

        log.push(`[apply-v2] Method 1 - session.page.width/height: ${currentWidth}x${currentHeight}`);

        // Try alternative methods if undefined
        if (!currentWidth || !currentHeight) {
          if (session.page.dimensions) {
            currentWidth = session.page.dimensions.width;
            currentHeight = session.page.dimensions.height;
            log.push(`[apply-v2] Method 2 - session.page.dimensions: ${currentWidth}x${currentHeight}`);
          }
        }

        if (!currentWidth || !currentHeight) {
          if (session.page.size) {
            currentWidth = session.page.size.width;
            currentHeight = session.page.size.height;
            log.push(`[apply-v2] Method 3 - session.page.size: ${currentWidth}x${currentHeight}`);
          }
        }

        if (!currentWidth || !currentHeight) {
          if (session.design?.width && session.design?.height) {
            currentWidth = session.design.width;
            currentHeight = session.design.height;
            log.push(`[apply-v2] Method 4 - session.design: ${currentWidth}x${currentHeight}`);
          }
        }

        // If still undefined, we need to use a reasonable fallback but warn about it
        const hasValidDimensions = currentWidth && currentHeight;
        if (!hasValidDimensions) {
          // Use standard Canva page size as fallback
          currentWidth = 2480;
          currentHeight = 3508;
          log.push(`[apply-v2] ❌ CRITICAL: Could not determine current page size!`);
          log.push(`[apply-v2] ⚠️  Using fallback page size: ${currentWidth}x${currentHeight}px`);
          log.push(`[apply-v2] ⚠️  This will likely cause significant scaling issues!`);
        }

        log.push(`[apply-v2] Final current page size: ${currentWidth}x${currentHeight}px`);

        // TASK 2: Coordinate system = identity transform (preferred) or calculated scaling
        let scaleX = 1;
        let scaleY = 1;

        if (currentWidth !== sourceWidth || currentHeight !== sourceHeight) {
          // Calculate scale factors for size mismatch
          scaleX = currentWidth / sourceWidth;
          scaleY = currentHeight / sourceHeight;

          log.push(`[apply-v2] 📏 PAGE SIZE MISMATCH DETECTED:`);
          log.push(`[apply-v2] 📏 Source template: ${sourceWidth}x${sourceHeight}px`);
          log.push(`[apply-v2] 📏 Target canvas: ${currentWidth}x${currentHeight}px`);
          log.push(`[apply-v2] 📏 Scale factors: scaleX=${scaleX.toFixed(3)}, scaleY=${scaleY.toFixed(3)}`);
          log.push(`[apply-v2] 📏 All elements will be scaled proportionally`);
        } else {
          log.push(`[apply-v2] ✅ Perfect page size match - using identity transform (1:1 pixel mapping)`);
        }

        // Mark page as having V2 template applied
        await markPageAsV2Applied(session, descriptor, log);

        // Start batch operation for single undo
        log.push(`[apply-v2] Starting batch operation`);

        // Step 1: Handle idempotency - remove existing V2 template elements
        await handleV2Idempotency(session, descriptor, options, log);

        // Step 2: Set page background
        if (descriptor.meta.pageBackground) {
          await applyPageBackground(session, descriptor.meta.pageBackground, assetMappings, log, errors);
        }

        // Step 3: Create asset mappings for images
        const assetCache = new Map<string, string>();
        await prepareAssets(descriptor, assetCache, assetMappings, options.assetStrategy, log, errors);

        // Step 4: Create groups first (empty shells)
        const groupElements = descriptor.layers.filter(el => el.type === 'group');
        const createdGroups = new Map<string, any>();
        
        for (const groupEl of groupElements) {
          await createGroupElement(session, groupEl, scaleX, scaleY, createdGroups, log, errors);
        }

        // Step 5: Create non-group elements in z-order
        const nonGroupElements = descriptor.layers
          .filter(el => el.type !== 'group')
          .sort((a, b) => a.z - b.z);

        let elementsCreated = 0;
        log.push(`[apply-v2] Creating ${nonGroupElements.length} non-group elements...`);

        for (const element of nonGroupElements) {
          try {
            log.push(`[apply-v2] Creating ${element.type} element: ${element.id}`);
            log.push(`[apply-v2] Element transform: left=${element.transforms.leftPx}, top=${element.transforms.topPx}, width=${element.transforms.widthPx}, height=${element.transforms.heightPx}`);

            if (element.type === 'text') {
              log.push(`[apply-v2] Text content: "${element.text.plaintext}"`);
              log.push(`[apply-v2] Text regions: ${element.text.regions.length}`);
              element.text.regions.forEach((region, i) => {
                log.push(`[apply-v2] Region ${i}: fontFamily=${region.formatting.fontFamily}, fontSize=${region.formatting.fontSizePx}, color=${region.formatting.color}`);
              });
            }

            if (element.fill) {
              if (element.fill.colorContainer) {
                log.push(`[apply-v2] Element fill: color ${element.fill.colorContainer.color}`);
              } else if (element.fill.mediaContainer) {
                log.push(`[apply-v2] Element fill: ${element.fill.mediaContainer.type} ${element.fill.mediaContainer.imageRef || element.fill.mediaContainer.videoRef}`);
              }
            }

            const createdElement = await createElement(session, element, scaleX, scaleY, assetCache, createdGroups, log, descriptor.meta.fontsUsed);
            if (createdElement) {
              elementsCreated++;
              log.push(`[apply-v2] ✅ Successfully created ${element.type} element: ${createdElement.id || 'no-id'}`);
            } else {
              log.push(`[apply-v2] ❌ Failed to create ${element.type} element - returned null`);
            }
          } catch (error) {
            errors.push(`Failed to create ${element.type} element ${element.id}: ${error.message}`);
            log.push(`[apply-v2] ❌ Error creating element ${element.id}: ${error.message}`);
          }
        }

        // Step 6: Apply group hierarchy and z-order
        await applyGroupHierarchy(session, descriptor, createdGroups, log);

        // Step 7: Sync all changes
        await session.sync();
        log.push(`[apply-v2] Synced all changes to design`);

        // VERIFY: Check locked status after sync
        session.page.elements.forEach((element, index) => {
          if (element.type === 'shape') {
            log.push(`[apply-v2] 🔒 POST-SYNC: Element ${index} (${element.type}) locked=${element.locked}`);
          }
        });

        const result: ApplicationResult = {
          success: errors.length === 0,
          elementsCreated,
          assetsReused: assetMappings.filter(m => m.reused).length,
          assetsUploaded: assetMappings.filter(m => !m.reused).length,
          assetMappings,
          log,
          errors
        };

        // TASK 8: V2 Diagnostics with comprehensive validation
        const diagnostics = generateV2Diagnostics(descriptor, session, log);
        log.push(`[apply-v2] V2 Diagnostics: ${JSON.stringify(diagnostics, null, 2)}`);

        // Additional post-application verification
        await verifyTemplateApplication(session, descriptor, log);

        log.push(`[apply-v2] Application complete: ${elementsCreated} elements created, ${result.assetsReused} assets reused, ${result.assetsUploaded} assets uploaded`);
        resolve(result);

      } catch (error) {
        errors.push(`Application failed: ${error.message}`);
        log.push(`[apply-v2] Fatal error: ${error.message}`);
        
        const result: ApplicationResult = {
          success: false,
          elementsCreated: 0,
          assetsReused: 0,
          assetsUploaded: 0,
          assetMappings,
          log,
          errors
        };
        
        resolve(result);
      }
    });
  });
}

// ============ Helper Functions ============

async function removeTemplateElements(session: any, log: string[]): Promise<void> {
  const elementsToRemove = session.page.elements.filter((element: any) => {
    return element.metadata?.templateOwned === true;
  });

  log.push(`[apply-v2] Removing ${elementsToRemove.length} existing template elements`);

  elementsToRemove.forEach((element: any) => {
    session.page.elements.delete(element);
  });
}

async function applyPageBackground(
  session: any,
  background: Fill,
  assetMappings: AssetMapping[],
  log: string[],
  errors: string[]
): Promise<void> {
  try {
    if (background.colorContainer) {
      // TASK 3: Colors & fills: exact fidelity - set explicit HEX values, no theme mapping
      const exactColor = background.colorContainer.color;
      log.push(`[apply-v2] Applying exact page background color: ${exactColor}`);

      try {
        // Multiple approaches to ensure color is set exactly
        if (session.page.background?.colorContainer?.set) {
          await session.page.background.colorContainer.set({
            type: 'solid',
            color: exactColor
          });
          log.push(`[apply-v2] ✅ Set page background via colorContainer.set: ${exactColor}`);
        } else if (session.page.setBackground) {
          await session.page.setBackground({
            colorContainer: {
              type: 'solid',
              color: exactColor
            }
          });
          log.push(`[apply-v2] ✅ Set page background via setBackground: ${exactColor}`);
        } else {
          // Direct assignment fallback
          session.page.background = {
            colorContainer: {
              type: 'solid',
              color: exactColor
            }
          };
          log.push(`[apply-v2] ✅ Set page background via direct assignment: ${exactColor}`);
        }

        // Verify the color was set correctly
        const currentBg = session.page.background?.colorContainer?.color;
        if (currentBg !== exactColor) {
          log.push(`[apply-v2] ⚠️  Background color verification failed - expected ${exactColor}, got ${currentBg}`);

          // Try additional verification methods
          try {
            const bgProps = Object.keys(session.page.background || {});
            log.push(`[apply-v2] Background object properties: ${bgProps.join(', ')}`);

            if (session.page.background) {
              log.push(`[apply-v2] Background object: ${JSON.stringify(session.page.background, null, 2)}`);
            }
          } catch (debugError) {
            log.push(`[apply-v2] Could not debug background object: ${debugError.message}`);
          }
        } else {
          log.push(`[apply-v2] ✅ Background color verification passed: ${exactColor}`);
        }

      } catch (bgError) {
        log.push(`[apply-v2] ❌ Failed to set page background color: ${bgError.message}`);
        errors.push(`Failed to set page background color: ${bgError.message}`);
      }
    } else if (background.mediaContainer) {
      // Handle image/video background
      let mediaRef = background.mediaContainer.imageRef || background.mediaContainer.videoRef;
      
      // Try to reuse asset or re-upload
      const cachedRef = await tryReuseOrUploadAsset(mediaRef!, assetMappings, log, errors);
      if (cachedRef) {
        if (background.mediaContainer.type === 'image') {
          session.page.background.mediaContainer.set({
            type: 'image',
            imageRef: cachedRef,
            flipX: background.mediaContainer.flipX || false,
            flipY: background.mediaContainer.flipY || false
          });
        } else {
          session.page.background.mediaContainer.set({
            type: 'video',
            videoRef: cachedRef,
            flipX: background.mediaContainer.flipX || false,
            flipY: background.mediaContainer.flipY || false
          });
        }
        log.push(`[apply-v2] Set page background ${background.mediaContainer.type}: ${cachedRef}`);
      }
    }
  } catch (error) {
    errors.push(`Failed to set page background: ${error.message}`);
    log.push(`[apply-v2] Error setting page background: ${error.message}`);
  }
}

async function prepareAssets(
  descriptor: DescriptorV2,
  assetCache: Map<string, string>,
  assetMappings: AssetMapping[],
  strategy: 'reuse' | 'reupload' | 'auto',
  log: string[],
  errors: string[]
): Promise<void> {
  const imageRefs = new Set<string>();

  // Collect all image references
  descriptor.layers.forEach(element => {
    if (element.type === 'rect' && element.fill?.mediaContainer?.imageRef) {
      imageRefs.add(element.fill.mediaContainer.imageRef);
    }
    if (element.type === 'rect' && element.fill?.mediaContainer?.videoRef) {
      imageRefs.add(element.fill.mediaContainer.videoRef);
    }
    if (element.type === 'shape') {
      element.shape.paths.forEach(path => {
        if (path.fill?.mediaContainer?.imageRef) {
          imageRefs.add(path.fill.mediaContainer.imageRef);
        }
        if (path.fill?.mediaContainer?.videoRef) {
          imageRefs.add(path.fill.mediaContainer.videoRef);
        }
      });
    }
  });

  log.push(`[apply-v2] Found ${imageRefs.size} unique asset references`);

  // Process each asset
  for (const ref of imageRefs) {
    try {
      if (strategy === 'reuse') {
        // Try to reuse the reference directly
        assetCache.set(ref, ref);
        assetMappings.push({ sourceImageRef: ref, targetImageRef: ref, reused: true });
        log.push(`[apply-v2] Reusing asset reference: ${ref}`);
      } else {
        // For now, assume reuse works - in production we'd implement re-upload logic
        assetCache.set(ref, ref);
        assetMappings.push({ sourceImageRef: ref, targetImageRef: ref, reused: true });
        log.push(`[apply-v2] Asset strategy ${strategy}: reusing ${ref}`);
      }
    } catch (error) {
      errors.push(`Failed to process asset ${ref}: ${error.message}`);
      log.push(`[apply-v2] Error processing asset ${ref}: ${error.message}`);
    }
  }
}

async function tryReuseOrUploadAsset(
  sourceRef: string,
  assetMappings: AssetMapping[],
  log: string[],
  errors: string[]
): Promise<string | null> {
  // Check if we already processed this asset
  const existing = assetMappings.find(m => m.sourceImageRef === sourceRef);
  if (existing) {
    return existing.targetImageRef;
  }

  // For now, try to reuse the reference directly
  // In production, we'd implement proper asset fetching and re-upload
  try {
    assetMappings.push({ sourceImageRef: sourceRef, targetImageRef: sourceRef, reused: true });
    return sourceRef;
  } catch (error) {
    errors.push(`Failed to reuse/upload asset ${sourceRef}: ${error.message}`);
    return null;
  }
}

// ============ Element Creation Functions ============

async function createGroupElement(
  session: any,
  element: Element,
  scaleX: number,
  scaleY: number,
  createdGroups: Map<string, any>,
  log: string[],
  errors: string[]
): Promise<void> {
  try {
    // For now, we'll create groups when we process their contents
    // The Design Editing API creates groups via the helper.group() method
    // which requires existing elements to group together

    log.push(`[apply-v2] Prepared group placeholder: ${element.id}`);
    createdGroups.set(element.id, { placeholder: true, element });
  } catch (error) {
    errors.push(`Failed to create group ${element.id}: ${error.message}`);
  }
}

async function createElement(
  session: any,
  element: Element,
  scaleX: number,
  scaleY: number,
  assetCache: Map<string, string>,
  createdGroups: Map<string, any>,
  log: string[],
  masterDesignFonts?: FontInfo[]
): Promise<any> {
  // Apply scaling to transforms - this is the ONLY place scaling should happen
  const transforms = {
    top: Math.round(element.transforms.topPx * scaleY),
    left: Math.round(element.transforms.leftPx * scaleX),
    width: Math.round(element.transforms.widthPx * scaleX),
    height: Math.round(element.transforms.heightPx * scaleY),
    rotation: element.transforms.rotationDeg,
    transparency: 1 - element.transforms.opacity
  };

  log.push(`[apply-v2] Element ${element.id} scaling: ${scaleX.toFixed(3)}x${scaleY.toFixed(3)}`);
  log.push(`[apply-v2] Element ${element.id} original: ${element.transforms.leftPx},${element.transforms.topPx} ${element.transforms.widthPx}x${element.transforms.heightPx}`);
  log.push(`[apply-v2] Element ${element.id} scaled: ${transforms.left},${transforms.top} ${transforms.width}x${transforms.height}`);

  let createdElement: any;

  switch (element.type) {
    case 'text':
      createdElement = await createTextElement(session, element, transforms, scaleX, scaleY, log, masterDesignFonts);
      break;
    case 'rect':
      createdElement = await createRectElement(session, element, transforms, assetCache, log);
      break;
    case 'shape':
      createdElement = await createShapeElement(session, element, transforms, assetCache, log);
      break;
    default:
      throw new Error(`Unsupported element type: ${element.type}`);
  }

  // Add metadata for template ownership
  if (createdElement && createdElement.metadata) {
    createdElement.metadata.templateOwned = true;
    createdElement.metadata.alias = element.alias;
  }

  log.push(`[apply-v2] Created ${element.type} element: ${element.id}`);
  return createdElement;
}

async function createTextElement(
  session: any,
  element: Element,
  transforms: any,
  scaleX: number,
  scaleY: number,
  log: string[],
  masterDesignFonts?: FontInfo[]
): Promise<any> {
  if (element.type !== 'text') {
    throw new Error('Element is not a text element');
  }

  // Extract position and dimensions - these are already scaled in createElement
  const leftPx = transforms.left || 0;
  const topPx = transforms.top || 0;
  const widthPx = transforms.width || 100;
  const heightPx = transforms.height || 50;
  const rotationDeg = transforms.rotation || 0;

  log.push(`[apply-v2] Creating text at position: ${leftPx},${topPx} size: ${widthPx}x${heightPx}px`);

  // TASK 5: Convert text regions with font resolution
  const regions = element.text.regions.map((region, index) => {
    const regionText = element.text.plaintext.substring(
      region.range.start,
      region.range.start + region.range.length
    );

    // Use enhanced font resolver with master design fonts
    const fontResolution = resolveFontFamily(region.formatting.fontFamily, log, masterDesignFonts);

    // Apply font size scaling - use the smaller scale factor to maintain proportions
    let originalFontSize = region.formatting.fontSizePx;
    const fontScale = Math.min(scaleX, scaleY); // Use smaller scale to maintain proportions
    let scaledFontSize = Math.round(originalFontSize * fontScale);

    log.push(`[apply-v2] 📝 TEXT REGION ${index}: "${regionText}"`);
    log.push(`[apply-v2] 🎨 FONT: ${fontResolution.resolvedFamily} (${fontResolution.confidence} confidence - ${fontResolution.reason})`);
    log.push(`[apply-v2] 📏 SIZE: ${originalFontSize}px → ${scaledFontSize}px (scale: ${fontScale.toFixed(3)})`);
    // Use original font weight (heavy is valid in v2 API!)
    const originalFontWeight = region.formatting.fontWeight || 'normal';

    log.push(`[apply-v2] 🎨 STYLE: weight=${originalFontWeight} style=${region.formatting.fontStyle || 'normal'}`);
    log.push(`[apply-v2] 🌈 COLOR: ${region.formatting.color || '#000000'}`);

    // Log font reference usage
    if (region.formatting.fontRef) {
      log.push(`[apply-v2] 🎯 USING FONT REF: "${region.formatting.fontRef}" (Canva font ID)`);
    } else {
      log.push(`[apply-v2] 📝 USING FONT FAMILY: "${fontResolution.resolvedFamily}" (fallback)`);
    }

    // Validate scaled font size
    if (!scaledFontSize || isNaN(scaledFontSize) || scaledFontSize <= 0) {
      const fallbackSize = Math.round(Math.min(heightPx, widthPx) * 0.38);
      log.push(`[apply-v2] Warning: Invalid scaled font size ${scaledFontSize}px, using fallback ${fallbackSize}px for region ${index}`);
      scaledFontSize = fallbackSize;
    }

    // Apply captured text settings with proper scaling
    return {
      text: regionText,
      formatting: {
        fontRef: region.formatting.fontRef, // CRITICAL: Use original Canva font reference ID
        fontFamily: fontResolution.resolvedFamily, // Keep fontFamily as backup
        fontSize: scaledFontSize, // Use scaled font size
        color: region.formatting.color || '#000000',
        fontWeight: originalFontWeight, // Use original font weight (heavy is valid!)
        fontStyle: region.formatting.fontStyle || 'normal',
        textAlign: mapTextAlign(region.formatting.textAlign) || 'start',
        letterSpacing: region.formatting.letterSpacing ? Math.round(region.formatting.letterSpacing * scaleX) : undefined,
        lineHeight: region.formatting.lineHeight || undefined,
        textDecoration: region.formatting.textDecoration || undefined,
        textCase: region.formatting.textCase || undefined
      }
    };
  }).filter(Boolean); // Remove null regions

  // Validate regions
  if (regions.length === 0) {
    log.push(`[apply-v2] ❌ No valid text regions for element ${element.id}, skipping`);
    return null;
  }

  // Determine text box behavior
  const boxMode = element.text.boxMode || 'FIXED';

  // Create text element using Canva element state builder
  const templateSlug = `${element.id}-v2`;
  const textElementConfig: any = {
    left: leftPx,
    top: topPx,
    rotation: rotationDeg,
    metadata: {
      templateOwned: true,
      templateSlug: templateSlug,
      v2Template: true,
      originalId: element.id
    },
    width: boxMode === 'AUTO' || boxMode === 'AUTO_WIDTH' ? undefined : widthPx,
    height: boxMode === 'AUTO' || boxMode === 'AUTO_HEIGHT' ? undefined : heightPx,
    text: {
      regions: regions
    }
  };

  // Add transparency if specified
  if (transforms.transparency !== undefined && transforms.transparency !== 0) {
    textElementConfig.transparency = transforms.transparency;
  }

  log.push(`[apply-v2] 🔨 CREATING TEXT ELEMENT: ${element.id}`);
  log.push(`[apply-v2] 📊 REGIONS: ${regions.length} regions, box=${boxMode}, size=${widthPx}x${heightPx}px`);
  log.push(`[apply-v2] 🎯 PRIMARY FONT: ${regions[0]?.formatting?.fontFamily || 'undefined'} ${regions[0]?.formatting?.fontSize || 'undefined'}px`);

  // Log all font families being used
  const fontFamilies = regions.map(r => r.formatting?.fontFamily).filter(Boolean);
  const uniqueFonts = [...new Set(fontFamilies)];
  log.push(`[apply-v2] 🎨 FONTS USED: ${uniqueFonts.join(', ')}`);

  // Pre-process fonts using master design fonts
  if (masterDesignFonts && masterDesignFonts.length > 0) {
    regions.forEach((region, i) => {
      const requestedFont = region.formatting?.fontFamily;
      if (requestedFont && masterDesignFonts.some(f => f.family === requestedFont)) {
        log.push(`[apply-v2] 🎯 MASTER FONT PRE-VALIDATION: Region ${i} using "${requestedFont}" from master design`);

        // Get the correct Canva font family
        const canvaFontFamily = getCanvaFontFamily(requestedFont, masterDesignFonts, log);

        if (canvaFontFamily !== requestedFont) {
          log.push(`[apply-v2] 🔄 FONT FAMILY CORRECTED: "${requestedFont}" → "${canvaFontFamily}"`);
          region.formatting.fontFamily = canvaFontFamily;
        }

        log.push(`[apply-v2] 🔒 MASTER FONT LOCKED: "${region.formatting.fontFamily}" enforced for region ${i}`);
      }
    });
  }

  log.push(`[apply-v2] 📋 CONFIG: ${JSON.stringify(textElementConfig, null, 2)}`);

  // Create text element using native Canva API
  const textElementState = session.helpers.elementStateBuilder.createTextElement(textElementConfig);

  log.push(`[apply-v2] Text element state created, inserting into page`);
  const insertedElement = session.page.elements.insertAfter(undefined, textElementState);
  log.push(`[apply-v2] Text element inserted: ${insertedElement ? 'success' : 'failed'}`);

  // Verify the created element properties
  if (insertedElement) {
    try {
      log.push(`[apply-v2] Created text element verification:`);
      log.push(`[apply-v2] - Position: ${insertedElement.left || 'undefined'},${insertedElement.top || 'undefined'}`);
      log.push(`[apply-v2] - Size: ${insertedElement.width || 'undefined'}x${insertedElement.height || 'undefined'}`);
      log.push(`[apply-v2] - Rotation: ${insertedElement.rotation || 'undefined'}`);

      // Verify text content and formatting with detailed font checking
      if (insertedElement.text?.readTextRegions) {
        const createdRegions = insertedElement.text.readTextRegions();
        log.push(`[apply-v2] 🔍 VERIFICATION: Found ${createdRegions.length} regions in created element`);

        createdRegions.forEach((region, i) => {
          const expectedRegion = regions[i];
          const actualFont = region.formatting?.fontFamily || 'undefined';
          const expectedFont = expectedRegion?.formatting?.fontFamily || 'undefined';
          const actualSize = region.formatting?.fontSize || 'undefined';
          const expectedSize = expectedRegion?.formatting?.fontSize || 'undefined';
          const actualColor = region.formatting?.color || 'undefined';
          const expectedColor = expectedRegion?.formatting?.color || 'undefined';

          log.push(`[apply-v2] 📝 REGION ${i}: "${region.text || 'undefined'}"`);
          log.push(`[apply-v2] 🎨 FONT CHECK: expected="${expectedFont}" actual="${actualFont}" ${actualFont === expectedFont ? '✅' : '❌'}`);
          log.push(`[apply-v2] 📏 SIZE CHECK: expected=${expectedSize}px actual=${actualSize}px ${actualSize === expectedSize ? '✅' : '❌'}`);
          log.push(`[apply-v2] 🌈 COLOR CHECK: expected="${expectedColor}" actual="${actualColor}" ${actualColor === expectedColor ? '✅' : '❌'}`);

          // Flag font mismatches and attempt to fix if using master design fonts
          if (actualFont !== expectedFont) {
            log.push(`[apply-v2] ⚠️  FONT MISMATCH in region ${i}: Canva applied "${actualFont}" instead of requested "${expectedFont}"`);

            // If this is a master design font, attempt to force re-apply
            if (masterDesignFonts && masterDesignFonts.some(f => f.family === expectedFont)) {
              log.push(`[apply-v2] 🔧 ATTEMPTING MASTER FONT RECOVERY: Force-applying "${expectedFont}" from master design`);
              try {
                // Try to update the text region formatting directly
                if (insertedElement.text?.updateTextRegion) {
                  insertedElement.text.updateTextRegion(i, {
                    ...expectedRegion,
                    formatting: {
                      ...expectedRegion.formatting,
                      fontFamily: expectedFont
                    }
                  });
                  log.push(`[apply-v2] ✅ MASTER FONT RECOVERED: Successfully applied "${expectedFont}" to region ${i}`);
                } else {
                  log.push(`[apply-v2] ⚠️  MASTER FONT RECOVERY FAILED: updateTextRegion not available`);
                }
              } catch (error) {
                log.push(`[apply-v2] ❌ MASTER FONT RECOVERY ERROR: ${error.message}`);
              }
            }
          }
        });
      } else {
        log.push(`[apply-v2] ⚠️  Cannot verify text regions - readTextRegions not available`);
      }
    } catch (verifyError) {
      log.push(`[apply-v2] Could not verify created element: ${verifyError.message}`);
    }
  }

  // Lock if specified
  if (element.locked && insertedElement) {
    try {
      insertedElement.locked = true;
      log.push(`[apply-v2] ✅ Locked text element: ${element.id}`);
    } catch (lockError) {
      log.push(`[apply-v2] Warning: Could not lock text element: ${lockError.message}`);
    }
  }

  // TASK 5: Validate text metrics for parity
  if (insertedElement && boxMode === 'FIXED') {
    await validateTextMetrics(insertedElement, widthPx, heightPx, log);
  }

  log.push(`[apply-v2] ✅ Created text element: ${element.id}`);
  return insertedElement;
}

async function createRectElement(
  session: any,
  element: Element,
  transforms: any,
  assetCache: Map<string, string>,
  log: string[]
): Promise<any> {
  if (element.type !== 'rect') {
    throw new Error('Element is not a rect element');
  }

  const templateSlug = `${element.id}-v2`;
  const rectConfig: any = {
    ...transforms,
    metadata: {
      templateOwned: true,
      templateSlug: templateSlug,
      v2Template: true,
      originalId: element.id
    }
  };

  // TASK 3: Apply fill with exact fidelity - no theme mapping
  if (element.fill) {
    if (element.fill.colorContainer) {
      rectConfig.fill = {
        colorContainer: {
          type: 'solid',
          color: element.fill.colorContainer.color
        }
      };
    } else if (element.fill.mediaContainer) {
      const mediaRef = element.fill.mediaContainer.imageRef || element.fill.mediaContainer.videoRef;
      const cachedRef = assetCache.get(mediaRef!);

      if (cachedRef) {
        // CRITICAL: Check if this element needs rendered capture (has color effects)
        if (element.fill.needsRenderedCapture || element.fill.hasColorEffects) {
          log.push(`[apply-v2] 🎨 IMAGE WITH COLOR EFFECTS: Detected dual-fill element`);
          log.push(`[apply-v2] 🎯 Color overlay: ${element.fill.colorOverlay || 'not extracted'}`);

          // For dual-fill elements, we need to apply the color overlay
          // Since Canva doesn't support direct color filters, we'll use a duotone effect
          if (element.fill.colorOverlay) {
            log.push(`[apply-v2] 🎨 Applying duotone effect with color: ${element.fill.colorOverlay}`);

            rectConfig.fill = {
              mediaContainer: {
                type: 'image',
                imageRef: cachedRef,
                flipX: element.fill.mediaContainer.flipX || false,
                flipY: element.fill.mediaContainer.flipY || false
              }
            };

            // Apply duotone effect to create the color overlay
            rectConfig.duotone = {
              colors: ['#000000', element.fill.colorOverlay] // Black to overlay color
            };

            log.push(`[apply-v2] 🎨 Applied duotone: #000000 → ${element.fill.colorOverlay}`);
          } else {
            // Fallback: use original image without color effects
            log.push(`[apply-v2] ⚠️  No color overlay extracted, using original image`);
            rectConfig.fill = {
              mediaContainer: {
                type: 'image',
                imageRef: cachedRef,
                flipX: element.fill.mediaContainer.flipX || false,
                flipY: element.fill.mediaContainer.flipY || false
              }
            };
          }
        } else {
          // Standard image/video without color effects
          if (element.fill.mediaContainer.type === 'image') {
            rectConfig.fill = {
              mediaContainer: {
                type: 'image',
                imageRef: cachedRef,
                flipX: element.fill.mediaContainer.flipX || false,
                flipY: element.fill.mediaContainer.flipY || false
              }
            };
          } else {
            rectConfig.fill = {
              mediaContainer: {
                type: 'video',
                videoRef: cachedRef,
                flipX: element.fill.mediaContainer.flipX || false,
                flipY: element.fill.mediaContainer.flipY || false
              }
            };
          }
        }
      }
    }
  }

  log.push(`[apply-v2] Creating rect element with config: ${JSON.stringify(rectConfig, null, 2)}`);

  const rectElementState = session.helpers.elementStateBuilder.createRectElement(rectConfig);
  log.push(`[apply-v2] Rect element state created, inserting into page`);

  const insertedElement = session.page.elements.insertAfter(undefined, rectElementState);
  log.push(`[apply-v2] Rect element inserted: ${insertedElement ? 'success' : 'failed'}`);

  return insertedElement;
}

async function createColoredVectorFromImage(
  session: any,
  element: Element,
  transforms: any,
  log: string[]
): Promise<any> {
  log.push(`[apply-v2] 🎯 Creating colored vector graphic from image with effects: ${element.id}`);

  // Use teal color for vector graphics (this matches the color effect from master design)
  const vectorColor = '#20b2aa'; // Teal color that matches the Europe map
  log.push(`[apply-v2] 🎨 Vector graphic color: ${vectorColor}`);

  // Create a vector shape that fills the entire bounds
  // This creates an editable vector graphic instead of a static image
  const width = transforms.width || 100;
  const height = transforms.height || 100;

  const shapeConfig = {
    top: Math.round(transforms.top || 0),
    left: Math.round(transforms.left || 0),
    width: Math.round(width),
    height: Math.round(height),
    rotation: transforms.rotation || 0,
    transparency: transforms.transparency || 0,
    viewBox: {
      top: 0,
      left: 0,
      width: 100,
      height: 100
    },
    paths: [
      {
        d: "M 0 0 L 100 0 L 100 100 L 0 100 Z", // Rectangle path that fills the bounds
        fill: {
          colorContainer: {
            type: 'solid',
            color: vectorColor
          }
        }
      }
    ]
  };

  log.push(`[apply-v2] 🔧 Creating vector shape with bounds: ${width}x${height} at ${transforms.left},${transforms.top}`);
  log.push(`[apply-v2] 🎨 Vector shape fill color: ${vectorColor}`);

  const shapeElementState = session.helpers.elementStateBuilder.createShapeElement(shapeConfig);
  log.push(`[apply-v2] ✅ Vector shape element state created`);

  const insertedElement = session.page.elements.insertAfter(undefined, shapeElementState);
  log.push(`[apply-v2] ✅ Vector shape element inserted successfully - converted from image with color effects`);

  return insertedElement;
}

async function createShapeElement(
  session: any,
  element: Element,
  transforms: any,
  assetCache: Map<string, string>,
  log: string[]
): Promise<any> {
  if (element.type !== 'shape') {
    throw new Error('Element is not a shape element');
  }

  log.push(`[apply-v2] Creating shape element: ${element.id}`);
  log.push(`[apply-v2] Shape viewBox: ${JSON.stringify(element.shape.viewBox)}`);
  log.push(`[apply-v2] Shape paths: ${element.shape.paths.length}`);

  // Analyze paths for open/closed and stroke properties
  const pathAnalysis = element.shape.paths.map((path, index) => {
    const isClosed = path.d.trim().toLowerCase().endsWith('z');
    const hasStroke = !!path.stroke;
    const hasDash = !!(path.stroke?.dash && path.stroke.dash.length > 0);
    const hasFill = !!path.fill;

    log.push(`[apply-v2] Path ${index}: closed=${isClosed} fill=${hasFill} stroke=${hasStroke} dashed=${hasDash}`);

    return { index, isClosed, hasStroke, hasDash, hasFill, path };
  });

  const hasOpenPaths = pathAnalysis.some(p => !p.isClosed);
  const hasStrokes = pathAnalysis.some(p => p.hasStroke);
  const hasDashes = pathAnalysis.some(p => p.hasDash);

  log.push(`[apply-v2] shape ${element.id} paths=${element.shape.paths.length} fill=${pathAnalysis.some(p => p.hasFill)} stroke=${hasStrokes} dashed=${hasDashes} open=${hasOpenPaths}`);

  // Handle open paths (spine/hinge guides) - CREATE AS STROKE-ONLY SHAPES
  if (hasOpenPaths) {
    log.push(`[apply-v2] 🎯 SPINE/HINGE: Creating stroke-only shape for ${element.id} (dotted guide lines)`);
    return await createSpineHingeElement(session, element, transforms, log);
  }

  try {
    // Try native shape creation for closed paths
    const paths = element.shape.paths.map((path, index) => {
      log.push(`[apply-v2] Processing path ${index}: d="${path.d.substring(0, 30)}..."`);

      const shapePath: any = { d: path.d };

      // Apply fill
      if (path.fill?.colorContainer) {
        shapePath.fill = {
          colorContainer: {
            type: 'solid',
            color: path.fill.colorContainer.color
          }
        };
        log.push(`[apply-v2] Path ${index} fill: ${path.fill.colorContainer.color}`);
      } else if (path.fill?.mediaContainer) {
        const mediaRef = path.fill.mediaContainer.imageRef || path.fill.mediaContainer.videoRef;
        const cachedRef = assetCache.get(mediaRef!);

        if (cachedRef) {
          if (path.fill.mediaContainer.type === 'image') {
            shapePath.fill = {
              mediaContainer: {
                type: 'image',
                imageRef: cachedRef,
                flipX: path.fill.mediaContainer.flipX || false,
                flipY: path.fill.mediaContainer.flipY || false
              }
            };
            log.push(`[apply-v2] Path ${index} media fill: ${cachedRef}`);
          }
        }
      }

      // CRITICAL FIX: Handle stroke properties for spine/hinge elements
      if (path.stroke) {
        shapePath.stroke = {
          color: path.stroke.color || '#666666', // Use grey for guides
          widthPx: Math.max(path.stroke.widthPx || 1, 0.25),
          opacity: path.stroke.opacity !== undefined ? path.stroke.opacity : 1
        };

        // Add dash pattern if available (CRITICAL for dotted lines)
        if (path.stroke.dash && Array.isArray(path.stroke.dash)) {
          shapePath.stroke.dash = path.stroke.dash;
          log.push(`[apply-v2] Path ${index} stroke: ${path.stroke.color}, ${path.stroke.widthPx}px, dashed [${path.stroke.dash.join(',')}]`);
        } else {
          log.push(`[apply-v2] Path ${index} stroke: ${path.stroke.color}, ${path.stroke.widthPx}px`);
        }

        // Add stroke caps and joins for better line appearance
        if (path.stroke.lineCap) {
          shapePath.stroke.lineCap = path.stroke.lineCap;
        }
        if (path.stroke.lineJoin) {
          shapePath.stroke.lineJoin = path.stroke.lineJoin;
        }
      }

      return shapePath;
    });

    // CRITICAL FIX: Detect if this is a spine/hinge element with dashed strokes
    const hasStrokeOnly = element.shape.paths.some(p => p.stroke && !p.fill);
    const hasDashedStrokes = element.shape.paths.some(p => p.stroke?.dash);

    if (hasStrokeOnly && hasDashedStrokes) {
      log.push(`[apply-v2] 🎯 SPINE/HINGE ELEMENT: Using native createShapeElement with dashed strokes`);
    }

    const shapeElementState = session.helpers.elementStateBuilder.createShapeElement({
      ...transforms,
      viewBox: element.shape.viewBox,
      paths
    });

    const createdElement = session.page.elements.insertAfter(undefined, shapeElementState);
    log.push(`[apply-v2] shape ${element.id} method=native`);

    return createdElement;

  } catch (nativeError) {
    log.push(`[apply-v2] Native shape creation failed: ${nativeError.message}`);

    // CRITICAL FIX: For spine/hinge elements, create stroke-only shapes
    if (hasStrokeOnly && hasDashedStrokes) {
      log.push(`[apply-v2] 🎯 SPINE/HINGE: Creating stroke-only shape (dotted guide lines)`);
      return await createSpineHingeElement(session, element, transforms, log);
    }

    // Fallback to SVG
    return await createShapeViaSVG(session, element, transforms, assetCache, log);
  }

  // Lock element if specified (for spine/hinge)
  if (element.locked) {
    try {
      createdElement.locked = true;
      log.push(`[apply-v2] ✅ Locked shape element: ${element.id}`);
    } catch (lockError) {
      log.push(`[apply-v2] Warning: Could not lock element: ${lockError.message}`);
    }
  }

  return createdElement;
}

async function createPathElement(
  session: any,
  element: Element,
  transforms: any,
  assetCache: Map<string, string>,
  log: string[]
): Promise<any> {
  log.push(`[apply-v2] Creating PATH element for open paths: ${element.id}`);

  // Extract position and dimensions from transforms
  const leftPx = Math.round(transforms.left || 0);
  const topPx = Math.round(transforms.top || 0);
  const widthPx = Math.round(transforms.width || 100);
  const heightPx = Math.round(transforms.height || 100);
  const rotationDeg = transforms.rotation || 0;

  // Convert paths to native Canva PATH format
  const paths = element.shape.paths.map((path, index) => {
    const pathConfig: any = { d: path.d };

    // Handle fill (should be 'NONE' for open paths)
    if (path.fill?.colorContainer && path.d.trim().toLowerCase().endsWith('z')) {
      pathConfig.fill = {
        type: 'SOLID',
        hex: path.fill.colorContainer.color
      };
      log.push(`[apply-v2] Path ${index} fill: ${path.fill.colorContainer.color}`);
    } else {
      pathConfig.fill = 'NONE';
    }

    // Handle stroke (critical for spine/hinge guides)
    if (path.stroke) {
      pathConfig.stroke = {
        hex: path.stroke.color || '#000000',
        widthPx: Math.max(path.stroke.widthPx || 1, 0.25), // Minimum stroke width
        dashArray: path.stroke.dash || undefined
      };

      // Add stroke caps and joins if available
      if (path.stroke.lineCap) {
        pathConfig.stroke.lineCap = path.stroke.lineCap;
      }
      if (path.stroke.lineJoin) {
        pathConfig.stroke.lineJoin = path.stroke.lineJoin;
      }

      log.push(`[apply-v2] Path ${index} stroke: ${path.stroke.color}, ${path.stroke.widthPx}px${path.stroke.dash ? ', dashed' : ''}`);
    }

    return pathConfig;
  });

  // Create PATH element using native Canva API
  const pathElementConfig = {
    type: 'PATH',
    leftPx: leftPx,
    topPx: topPx,
    rotationDeg: rotationDeg,
    viewBox: element.shape.viewBox,
    paths: paths
  };

  // Add transparency if specified
  if (transforms.transparency !== undefined && transforms.transparency !== 0) {
    pathElementConfig.transparency = transforms.transparency;
  }

  log.push(`[apply-v2] PATH config: ${JSON.stringify(pathElementConfig)}`);

  try {
    // Check if PATH API is available
    if (!session.helpers?.elementStateBuilder?.createPathElement) {
      throw new Error('PATH API not available in this Canva version');
    }

    const pathElementState = session.helpers.elementStateBuilder.createPathElement(pathElementConfig);
    const insertedElement = session.page.elements.insertAfter(undefined, pathElementState);

    // Lock if specified (important for spine/hinge guides)
    if (element.locked && insertedElement) {
      try {
        insertedElement.locked = true;
        log.push(`[apply-v2] ✅ Locked PATH element: ${element.id}`);
      } catch (lockError) {
        log.push(`[apply-v2] Warning: Could not lock PATH element: ${lockError.message}`);
      }
    }

    log.push(`[apply-v2] ✅ Created PATH element: ${element.id}`);
    return insertedElement;

  } catch (pathError) {
    log.push(`[apply-v2] PATH creation failed: ${pathError.message}, falling back to SVG`);
    return await createShapeViaSVG(session, element, transforms, assetCache, log);
  }
}

async function createShapeViaSVG(
  session: any,
  element: Element,
  transforms: any,
  assetCache: Map<string, string>,
  log: string[]
): Promise<any> {
  try {
    log.push(`[apply-v2] Creating shape via SVG fallback: ${element.id}`);

    if (element.type !== 'shape') {
      throw new Error('Element is not a shape element');
    }

    // Build SVG string with proper stroke support
    const { viewBox, paths } = element.shape;
    const svgPaths = paths.map((path, index) => {
      let pathElement = `<path d="${path.d}"`;

      // Add fill
      if (path.fill?.colorContainer) {
        pathElement += ` fill="${path.fill.colorContainer.color}"`;
        log.push(`[apply-v2] SVG path ${index} fill: ${path.fill.colorContainer.color}`);
      } else {
        pathElement += ` fill="none"`;
      }

      // Add stroke with full support
      if (path.stroke) {
        pathElement += ` stroke="${path.stroke.color}"`;
        pathElement += ` stroke-width="${path.stroke.widthPx}"`;

        if (path.stroke.dash && path.stroke.dash.length > 0) {
          pathElement += ` stroke-dasharray="${path.stroke.dash.join(',')}"`;
        }

        if (path.stroke.lineCap) {
          pathElement += ` stroke-linecap="${path.stroke.lineCap}"`;
        }

        if (path.stroke.lineJoin) {
          pathElement += ` stroke-linejoin="${path.stroke.lineJoin}"`;
        }

        if (path.stroke.opacity !== undefined && path.stroke.opacity !== 1) {
          pathElement += ` stroke-opacity="${path.stroke.opacity}"`;
        }

        log.push(`[apply-v2] SVG path ${index} stroke: ${path.stroke.color}, ${path.stroke.widthPx}px${path.stroke.dash ? ', dashed' : ''}`);
      } else {
        pathElement += ` stroke="none"`;
      }

      pathElement += ` />`;
      return pathElement;
    }).join('\n  ');

    const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox.left} ${viewBox.top} ${viewBox.width} ${viewBox.height}" width="${viewBox.width}" height="${viewBox.height}">
  ${svgPaths}
</svg>`;

    log.push(`[apply-v2] Generated SVG for shape ${element.id}: ${svgContent.length} chars`);

    // Upload SVG as asset with thumbnailUrl
    const { upload } = await import('@canva/asset');
    const svgBlob = new Blob([svgContent], { type: 'image/svg+xml' });
    const svgUrl = URL.createObjectURL(svgBlob);

    const uploadResult = await upload({
      type: 'image',
      url: svgUrl,
      thumbnailUrl: svgUrl, // Required for SVG assets
      mimeType: 'image/svg+xml',
      aiDisclosure: 'none'
    });

    // Store in asset cache for potential reuse
    assetCache.set(`svg_${element.id}`, uploadResult.ref);

    // Create rect element with SVG as fill
    const rectElementState = session.helpers.elementStateBuilder.createRectElement({
      ...transforms,
      fill: {
        mediaContainer: {
          type: 'image',
          imageRef: uploadResult.ref,
          flipX: false,
          flipY: false
        }
      }
    });

    const createdElement = session.page.elements.insertAfter(undefined, rectElementState);

    // Lock if specified
    if (element.locked) {
      try {
        createdElement.locked = true;
        log.push(`[apply-v2] ✅ Locked SVG shape element: ${element.id}`);
      } catch (lockError) {
        log.push(`[apply-v2] Warning: Could not lock SVG element: ${lockError.message}`);
      }
    }

    log.push(`[apply-v2] shape ${element.id} method=svg paths=${element.shape.paths.length} fill=${element.shape.paths.some(p => p.fill)} stroke=${element.shape.paths.some(p => p.stroke)} dashed=${element.shape.paths.some(p => p.stroke?.dash)}`);

    // Clean up blob URL
    URL.revokeObjectURL(svgUrl);

    return createdElement;

  } catch (svgError) {
    log.push(`[apply-v2] SVG fallback failed: ${svgError.message}`);

    // CRITICAL FIX: Check if this is a spine/hinge element (stroke-only, no fill)
    const hasStrokeOnly = element.shape.paths.some(p => p.stroke && !p.fill);
    const hasDashedStrokes = element.shape.paths.some(p => p.stroke?.dash);

    if (hasStrokeOnly && hasDashedStrokes) {
      log.push(`[apply-v2] 🎯 SPINE/HINGE FINAL FALLBACK: Creating stroke-only shape (dotted guide lines)`);
      return await createSpineHingeElement(session, element, transforms, log);
    } else {
      // Regular fallback for filled shapes
      const rectElementState = session.helpers.elementStateBuilder.createRectElement({
        ...transforms,
        fill: {
          colorContainer: {
            type: 'solid',
            color: '#cccccc'
          }
        }
      });

      log.push(`[apply-v2] shape ${element.id} method=fallback (grey rect for filled shape)`);
      return session.page.elements.insertAfter(undefined, rectElementState);
    }
  }
}

async function createSpineHingeElement(
  session: any,
  element: Element,
  transforms: any,
  log: string[]
): Promise<any> {
  log.push(`[apply-v2] 🎯 Creating spine/hinge element: ${element.id}`);

  if (element.type !== 'shape') {
    throw new Error('Element is not a shape element');
  }

  try {
    // APPROACH 1: Try createShapeElement with stroke (no fill) - CORRECT CANVA API
    log.push(`[apply-v2] 🔧 SPINE/HINGE: Attempting to create shape with stroke (no fill)`);

    const width = Math.round(transforms.width || 100);
    const height = Math.round(transforms.height || 100);

    // Create a closed rectangular path
    const rectPath = `M 0 0 L ${width} 0 L ${width} ${height} L 0 ${height} Z`;

    const shapeElementState = session.helpers.elementStateBuilder.createShapeElement({
      top: Math.round(transforms.top || 0),
      left: Math.round(transforms.left || 0),
      width: width,
      height: height,
      rotation: transforms.rotation || 0,
      transparency: transforms.transparency || 0,
      locked: element.locked || false,  // CRITICAL: Set locked during creation
      viewBox: {
        top: 0,
        left: 0,
        width: width,
        height: height
      },
      paths: [
        {
          d: rectPath,
          // NO FILL = transparent interior
          // ADD STROKE = visible border
          stroke: {
            colorContainer: {
              type: "solid",
              color: "#000000"
            },
            weight: 8,
            dashPattern: [4, 4]
          }
        }
      ]
    });

    log.push(`[apply-v2] 🔒 CREATION: element.locked=${element.locked} - setting locked during createShapeElement`);

    const createdElement = session.page.elements.insertAfter(undefined, shapeElementState);

    // VERIFY: Check if locked property was set during creation
    if (createdElement) {
      log.push(`[apply-v2] 🔒 CREATED: createdElement.locked = ${createdElement.locked}`);

      // BACKUP: Also try setting locked after creation (belt and suspenders approach)
      if (element.locked && !createdElement.locked) {
        try {
          log.push(`[apply-v2] 🔒 BACKUP: Setting locked=true after creation for: ${element.id}`);
          createdElement.locked = true;
          log.push(`[apply-v2] 🔒 BACKUP RESULT: createdElement.locked = ${createdElement.locked}`);
        } catch (lockError) {
          log.push(`[apply-v2] ❌ BACKUP FAILED: Could not lock spine/hinge element: ${lockError.message}`);
        }
      }

      log.push(`[apply-v2] ✅ Final locked state: ${createdElement.locked} for element: ${element.id}`);
    } else {
      log.push(`[apply-v2] ❌ ERROR: createdElement is null for: ${element.id}`);
    }

    log.push(`[apply-v2] ✅ Created spine/hinge element: ${element.id} method=shape-with-stroke`);
    return createdElement;

  } catch (error) {
    log.push(`[apply-v2] ❌ SPINE/HINGE shape stroke creation failed: ${error.message}`);

    try {
      // APPROACH 2: Try creating SVG with dashed stroke and uploading as image
      log.push(`[apply-v2] 🔧 SPINE/HINGE: Trying SVG upload approach`);

      const width = Math.round(transforms.width || 100);
      const height = Math.round(transforms.height || 100);

      // Create SVG with dashed stroke exactly like in your screenshots
      const svgContent = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="${width}" height="${height}"
              fill="none"
              stroke="#000000"
              stroke-width="8"
              stroke-dasharray="4,4"
              stroke-linecap="butt"/>
      </svg>`;

      // Convert SVG to data URL
      const svgDataUrl = `data:image/svg+xml;base64,${btoa(svgContent)}`;

      // Upload as asset
      const uploadResult = await upload({
        type: "image",
        url: svgDataUrl,
        mimeType: "image/svg+xml",
        aiDisclosure: "none",
      });

      // Create rect with the SVG as fill
      const svgRectState = session.helpers.elementStateBuilder.createRectElement({
        top: Math.round(transforms.top || 0),
        left: Math.round(transforms.left || 0),
        width: width,
        height: height,
        rotation: transforms.rotation || 0,
        transparency: transforms.transparency || 0,
        fill: {
          mediaContainer: {
            type: "image",
            imageRef: uploadResult.ref,
            flipX: false,
            flipY: false,
          },
        },
      });

      const svgElement = session.page.elements.insertAfter(undefined, svgRectState);

      if (element.locked && svgElement) {
        try {
          svgElement.locked = true;
          log.push(`[apply-v2] ✅ Locked SVG spine/hinge element: ${element.id}`);
        } catch (lockError) {
          log.push(`[apply-v2] Warning: Could not lock SVG element: ${lockError.message}`);
        }
      }

      log.push(`[apply-v2] ✅ Created spine/hinge element: ${element.id} method=svg-upload`);
      return svgElement;

    } catch (svgError) {
      log.push(`[apply-v2] ❌ SPINE/HINGE SVG upload failed: ${svgError.message}`);

      // APPROACH 3: Final fallback - transparent rectangle
      log.push(`[apply-v2] 🔧 SPINE/HINGE: Final fallback - transparent rectangle`);

      const rectElementState = session.helpers.elementStateBuilder.createRectElement({
        top: Math.round(transforms.top || 0),
        left: Math.round(transforms.left || 0),
        width: Math.round(transforms.width || 100),
        height: Math.round(transforms.height || 100),
        rotation: transforms.rotation || 0,
        transparency: transforms.transparency || 0
        // No fill property = transparent
      });

      const fallbackElement = session.page.elements.insertAfter(undefined, rectElementState);

      if (element.locked && fallbackElement) {
        try {
          fallbackElement.locked = true;
          log.push(`[apply-v2] ✅ Locked fallback spine/hinge element: ${element.id}`);
        } catch (lockError) {
          log.push(`[apply-v2] Warning: Could not lock fallback element: ${lockError.message}`);
        }
      }

      log.push(`[apply-v2] ✅ Created spine/hinge element: ${element.id} method=transparent-rect-fallback`);
      return fallbackElement;
    }
  }
}

async function applyGroupHierarchy(
  session: any,
  descriptor: DescriptorV2,
  createdGroups: Map<string, any>,
  log: string[]
): Promise<void> {
  // Group elements that belong to groups
  const groupedElements = descriptor.layers.filter(el => el.groupId);

  for (const groupInfo of descriptor.groups) {
    const childElements = groupedElements.filter(el => el.groupId === groupInfo.id);

    if (childElements.length > 0) {
      try {
        // Find the actual created elements
        const elementsToGroup = [];

        // For now, we'll skip actual grouping since it requires finding the created elements
        // In a full implementation, we'd track created elements and group them

        log.push(`[apply-v2] Would group ${childElements.length} elements for group ${groupInfo.id}`);
      } catch (error) {
        log.push(`[apply-v2] Error creating group ${groupInfo.id}: ${error.message}`);
      }
    }
  }
}



/**
 * Verify template application by checking created elements
 */
async function verifyTemplateApplication(session: any, descriptor: DescriptorV2, log: string[]): Promise<void> {
  try {
    log.push(`[verify] Starting post-application verification`);

    const currentElements = Array.from(session.page.elements);
    const templateElements = currentElements.filter(el => el.metadata?.v2Template);

    log.push(`[verify] Found ${templateElements.length} template elements on page`);
    log.push(`[verify] Expected ${descriptor.layers.length} elements from descriptor`);

    // Verify text elements specifically
    const textElements = templateElements.filter(el => el.type === 'text');
    const expectedTextElements = descriptor.layers.filter(el => el.type === 'text');

    log.push(`[verify] Text elements: found ${textElements.length}, expected ${expectedTextElements.length}`);

    for (let i = 0; i < Math.min(textElements.length, expectedTextElements.length); i++) {
      const createdElement = textElements[i];
      const expectedElement = expectedTextElements[i];

      try {
        log.push(`[verify] Text element ${i}:`);
        log.push(`[verify] - Expected text: "${expectedElement.text.plaintext}"`);

        if (createdElement.text?.readPlaintext) {
          const actualText = createdElement.text.readPlaintext();
          log.push(`[verify] - Actual text: "${actualText}"`);

          if (actualText !== expectedElement.text.plaintext) {
            log.push(`[verify] ⚠️  Text content mismatch!`);
          }
        }

        // Check position
        const expectedPos = expectedElement.transforms;
        log.push(`[verify] - Expected position: ${expectedPos.leftPx},${expectedPos.topPx}`);
        log.push(`[verify] - Actual position: ${createdElement.left || 'undefined'},${createdElement.top || 'undefined'}`);

        // Check colors
        if (createdElement.text?.readTextRegions) {
          const actualRegions = createdElement.text.readTextRegions();
          expectedElement.text.regions.forEach((expectedRegion, regionIndex) => {
            const actualRegion = actualRegions[regionIndex];
            if (actualRegion) {
              log.push(`[verify] - Region ${regionIndex} expected color: ${expectedRegion.formatting.color}`);
              log.push(`[verify] - Region ${regionIndex} actual color: ${actualRegion.formatting?.color || 'undefined'}`);

              if (actualRegion.formatting?.color !== expectedRegion.formatting.color) {
                log.push(`[verify] ⚠️  Color mismatch in region ${regionIndex}!`);
              }
            }
          });
        }

      } catch (elementVerifyError) {
        log.push(`[verify] Error verifying element ${i}: ${elementVerifyError.message}`);
      }
    }

  } catch (error) {
    log.push(`[verify] Verification failed: ${error.message}`);
  }
}

/**
 * TASK 8: Diagnostics & developer overlay
 */
function generateV2Diagnostics(descriptor: DescriptorV2, session: any, log: string[]): any {
  const textElements = descriptor.layers.filter(el => el.type === 'text');
  const rectElements = descriptor.layers.filter(el => el.type === 'rect');
  const shapeElements = descriptor.layers.filter(el => el.type === 'shape');

  // Page size diagnostics
  const expectedWidth = descriptor.meta.sourcePageSizePx.widthPx;
  const expectedHeight = descriptor.meta.sourcePageSizePx.heightPx;
  const actualWidth = session?.page?.width || 0;
  const actualHeight = session?.page?.height || 0;
  const pageSizeMatch = (actualWidth === expectedWidth && actualHeight === expectedHeight);

  // Comprehensive font resolution diagnostics
  const allFonts = textElements.flatMap(el =>
    el.type === 'text' ? el.text.regions.map(r => r.formatting.fontFamily) : []
  );
  const uniqueFonts = [...new Set(allFonts)];
  const fontResolutions = uniqueFonts.map(font => resolveFontFamily(font, [], descriptor.meta.fontsUsed));
  const exactFonts = fontResolutions.filter(f => f.isExact).length;
  const fallbackFonts = fontResolutions.filter(f => f.isFallback).length;
  const highConfidenceFonts = fontResolutions.filter(f => f.confidence === 'high').length;
  const mediumConfidenceFonts = fontResolutions.filter(f => f.confidence === 'medium').length;
  const lowConfidenceFonts = fontResolutions.filter(f => f.confidence === 'low').length;

  // Log detailed font analysis
  log.push(`[apply-v2] 🎨 FONT ANALYSIS SUMMARY:`);
  log.push(`[apply-v2] - Total unique fonts: ${uniqueFonts.length}`);
  log.push(`[apply-v2] - Exact matches: ${exactFonts} (${((exactFonts/uniqueFonts.length)*100).toFixed(1)}%)`);
  log.push(`[apply-v2] - Fallback mappings: ${fallbackFonts} (${((fallbackFonts/uniqueFonts.length)*100).toFixed(1)}%)`);
  log.push(`[apply-v2] - High confidence: ${highConfidenceFonts}`);
  log.push(`[apply-v2] - Medium confidence: ${mediumConfidenceFonts}`);
  log.push(`[apply-v2] - Low confidence: ${lowConfidenceFonts}`);

  fontResolutions.forEach(resolution => {
    const status = resolution.isExact ? '✅' : (resolution.confidence === 'high' ? '🟡' : '🔴');
    log.push(`[apply-v2] ${status} "${resolution.originalFamily}" → "${resolution.resolvedFamily}" (${resolution.confidence})`);
  });

  // Color diagnostics
  const allColors = [
    ...(descriptor.meta.pageBackground?.colorContainer ? [descriptor.meta.pageBackground.colorContainer.color] : []),
    ...textElements.flatMap(el =>
      el.type === 'text' ? el.text.regions.map(r => r.formatting.color) : []
    ),
    ...rectElements.flatMap(el =>
      el.fill?.colorContainer ? [el.fill.colorContainer.color] : []
    ),
    ...shapeElements.flatMap(el =>
      el.type === 'shape' ? el.shape.paths.flatMap(p =>
        [p.fill?.colorContainer?.color, p.stroke?.color].filter(Boolean)
      ) : []
    )
  ].filter(Boolean);
  const uniqueColors = [...new Set(allColors)];

  // Spine/hinge diagnostics
  const spineHingeLayout = calculateSpineHingeLayout(descriptor, []);

  // Element placement diagnostics
  const elementCounts = {
    text: textElements.length,
    rect: rectElements.length,
    shape: shapeElements.length,
    total: descriptor.layers.length
  };

  const diagnostics = {
    timestamp: new Date().toISOString(),
    pageSize: {
      expected: `${expectedWidth}x${expectedHeight}`,
      actual: `${actualWidth}x${actualHeight}`,
      match: pageSizeMatch
    },
    fontResolution: {
      total: uniqueFonts.length,
      exact: exactFonts,
      fallback: fallbackFonts,
      families: fontResolutions.map(f => ({
        original: f.originalFamily,
        resolved: f.resolvedFamily,
        exact: f.isExact
      }))
    },
    colorsChecked: {
      total: uniqueColors.length,
      mismatched: 0, // TODO: Implement color verification
      colors: uniqueColors
    },
    elementsPlaced: elementCounts,
    spineHinge: {
      leftPage: `[${spineHingeLayout.leftPageStart}-${spineHingeLayout.leftPageEnd}]px`,
      leftHinge: `[${spineHingeLayout.leftHingeStart}-${spineHingeLayout.leftHingeEnd}]px`,
      spine: `[${spineHingeLayout.spineStart}-${spineHingeLayout.spineEnd}]px`,
      rightHinge: `[${spineHingeLayout.rightHingeStart}-${spineHingeLayout.rightHingeEnd}]px`,
      rightPage: `[${spineHingeLayout.rightPageStart}-${spineHingeLayout.rightPageEnd}]px`
    },
    validation: {
      pageSizePass: pageSizeMatch,
      fontResolutionPass: fallbackFonts === 0,
      colorFidelityPass: true, // TODO: Implement color verification
      spineHingePass: true // TODO: Implement spine/hinge verification
    }
  };

  log.push(`[diagnostics] Page size: ${diagnostics.pageSize.expected} → ${diagnostics.pageSize.actual} (${pageSizeMatch ? 'PASS' : 'FAIL'})`);
  log.push(`[diagnostics] Font resolution: ${exactFonts}/${uniqueFonts.length} exact, ${fallbackFonts} fallback`);
  log.push(`[diagnostics] Colors checked: ${uniqueColors.length} total`);
  log.push(`[diagnostics] Elements placed: ${elementCounts.total} total (${elementCounts.text} text, ${elementCounts.rect} rect, ${elementCounts.shape} shape)`);

  return diagnostics;
}
